import { useState, useEffect, useRef } from "react";
import useWebSocket from "react-use-websocket";

/** Tempo sem novas mensagens para considerar o stream encerrado (servidor pode não fechar a conexão). */
const STREAM_IDLE_MS = 1500;

export const useWebsocketFetch = ({ onSuccess, onError, onEmpty }) => {
  const [socketUrl, setSocketUrl] = useState(null);
  const [status, setStatus] = useState("off");
  const [data, setData] = useState([]);
  const dataRef = useRef([]);
  const statusRef = useRef(status);
  const streamIdleTimerRef = useRef(null);

  dataRef.current = data;
  statusRef.current = status;

  const finishFetch = () => {
    if (streamIdleTimerRef.current) {
      clearTimeout(streamIdleTimerRef.current);
      streamIdleTimerRef.current = null;
    }
    const currentData = dataRef.current;
    if (currentData?.length !== 0) {
      onSuccess(currentData);
    }
    statusRef.current = "off";
    setStatus("off");
    dataRef.current = [];
    setData([]);
  };

  const { sendJsonMessage, lastJsonMessage } = useWebSocket(socketUrl, {
    onOpen: () => {
      if (streamIdleTimerRef.current) {
        clearTimeout(streamIdleTimerRef.current);
        streamIdleTimerRef.current = null;
      }
      setStatus("on");
      dataRef.current = [];
      setData([]);
    },
    onClose: () => {
      if (streamIdleTimerRef.current) {
        clearTimeout(streamIdleTimerRef.current);
        streamIdleTimerRef.current = null;
      }
      const currentData = dataRef.current;
      const currentStatus = statusRef.current;

      if (
        (currentData?.length === 0 && currentStatus === "fetching") ||
        (currentData?.length === 1 &&
          (currentData[0]?.MessageResponse === "Nenhum registro localizado." ||
            currentData[0]?.MessageResponse === "Falha em validação de filtros!"))
      ) {
        setStatus("empty");
        setStatus("off");
        dataRef.current = [];
        setData([]);
        return;
      }

      if (currentData?.length !== 0) {
        onSuccess(currentData);
        setStatus("off");
      }
      dataRef.current = [];
      setData([]);
    },
    onError: (error) => {
      setStatus("error");
      if (onError) {
        onError(error);
      }
    },
    shouldReconnect: () => true,
    reconnectInterval: 2000,
  });

  const handleFetchData = (data) => {
    statusRef.current = "fetching";
    setStatus("fetching");
    sendJsonMessage(data);
  };

  const handleFetchUrl = (url) => {
    setSocketUrl(url);
  };

  const onMessageReceive = (response) => {
    if (
      !response?.Result ||
      response?.message === "Forbidden" ||
      response?.Result[0]?.MessageResponse === "Nenhum registro localizado." ||
      response?.Result[0]?.MessageResponse === "Falha em validação de filtros!"
    ) {
      if (streamIdleTimerRef.current) {
        clearTimeout(streamIdleTimerRef.current);
        streamIdleTimerRef.current = null;
      }
      // Se já temos dados (ex.: última mensagem veio sem Result), finalizar com sucesso
      if (dataRef.current?.length > 0) {
        finishFetch();
        return;
      }
      setStatus("empty");
      setStatus("off");
      if (onEmpty) {
        onEmpty();
      }
      return;
    }
    const chunk = response?.Result ?? [];
    if (streamIdleTimerRef.current) {
      clearTimeout(streamIdleTimerRef.current);
    }
    streamIdleTimerRef.current = setTimeout(() => {
      streamIdleTimerRef.current = null;
      finishFetch();
    }, STREAM_IDLE_MS);
    setData((prevData) => {
      const next = [...prevData, ...chunk];
      dataRef.current = next; // ref atualizado na hora para onClose não ver dados desatualizados
      return next;
    });
  };

  const cancel = () => {
    sendJsonMessage({ status: "inactive" });
  };

  useEffect(() => {
    if (lastJsonMessage) {
      onMessageReceive(lastJsonMessage);
    }
  }, [lastJsonMessage]);

  useEffect(() => {
    return () => {
      if (streamIdleTimerRef.current) {
        clearTimeout(streamIdleTimerRef.current);
        streamIdleTimerRef.current = null;
      }
    };
  }, []);

  return {
    handleFetchData,
    handleFetchUrl,
    data,
    status,
    cancel,
  };
};
